import {
  buttonBaseClasses,
  dividerClasses,
  inputBaseClasses,
  menuItemClasses,
} from '@mui/material';
import { buttonClasses } from '@mui/material/Button';
import { Components, PaletteColor, Theme } from '@mui/material/styles';
import { pickersDayClasses } from '@mui/x-date-pickers';

import { alertErrorIcon, alertSuccessIcon } from '@/components/common/Alert';
import {
  checkboxCheckedIcon,
  checkboxUncheckedIcon,
  checkboxIndeterminateIcon,
} from '@/components/common/Checkbox';
import { DatePickerOpenIcon } from '@/components/common/DatePicker';
import { LinkComponentMapper } from '@/components/common/LinkComponentMapper';
import { selectIcon, SelectIcon } from '@/components/common/Select';
import { CALENDAR_DAY_SIZE } from '@/theme/consts';
import { tx } from '@/theme/tokens';

// The Tradehaus design language on MUI (docs/design-language/README.md, "Component overrides"). Surfaces, ink and
// light come from the CSS variables in theme/tokens.css (`tx`), so one override serves Light, Natural and Dark.
// Rules of thumb: tonal fills, never outlines; lime is the one action; milled (debossed) means a place or a selection;
// only floating layers are raised; no ripples, a press is a 0.985 scale.

const STATUS_COLORS = ['secondary', 'success', 'error', 'info', 'warning'] as const;

const pressable = {
  transition: `background-color ${tx.ease} .15s, color ${tx.ease} .15s, transform .1s ease, box-shadow .15s ease`,
  '&:active': { transform: 'scale(0.985)' },
};

// The selected tab / segment / chip / day: a pressed well on Light and Natural, a lifted tile on Dark
const selected = {
  background: tx.selBg,
  boxShadow: tx.selShadow,
  color: tx.text,
};

// A floating layer: menus, popovers, pickers
const floating = {
  backgroundColor: tx.panel,
  backgroundImage: tx.surfaceLight,
  backdropFilter: tx.blurPanel,
  WebkitBackdropFilter: tx.blurPanel,
  boxShadow: tx.shadow,
  color: tx.text,
};

export const components: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: {
      '*': {
        WebkitTapHighlightColor: 'transparent',
      },
      'a, button, [role="button"], [tabindex], input, textarea, select, summary': {
        WebkitTapHighlightColor: 'transparent',
      },
      html: {
        WebkitTapHighlightColor: 'transparent',
        '& ::-webkit-scrollbar': {
          width: '5px',
        },
        '& ::-webkit-scrollbar-thumb': {
          borderRadius: '4px',
          backgroundColor: tx.fill3,
        },
        '& ::-webkit-scrollbar-thumb:hover': {
          backgroundColor: tx.faint,
        },
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&:hover::-webkit-scrollbar': {
          display: 'block',
        },
        '& ::-webkit-scrollbar-track': {
          WebkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
        },
      },
      // tokens.css paints the ground, its light and the grain; CssBaseline must not cover them with a flat colour
      body: {
        backgroundColor: tx.ground,
        backgroundImage: 'var(--tx-ground-light)',
        backgroundAttachment: 'fixed',
        fontVariantNumeric: 'tabular-nums',
      },
      'body *': {
        textUnderlineOffset: '0.25em',
        textDecorationThickness: '0.075em',
        marginBlock: 0,
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...(ownerState.component === 'label' && {
          color: theme.palette.text.secondary,
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      component: LinkComponentMapper,
      underline: 'hover',
      color: 'inherit',
      fontWeight: 600,
    },
    styleOverrides: {
      root: {
        cursor: 'pointer',
      },
    },
  },
  // Tooltips are dark glass in every theme
  MuiTooltip: {
    defaultProps: {
      enterTouchDelay: 100,
      enterDelay: 120,
      slotProps: {
        popper: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'clippingAncestors',
                altAxis: true,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
          ],
        },
      },
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: tx.tooltip,
        backdropFilter: 'blur(10px) saturate(1.25)',
        WebkitBackdropFilter: 'blur(10px) saturate(1.25)',
        color: '#F7F7F6',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1.4,
        padding: '8px 12px',
        borderRadius: 12,
        maxWidth: 260,
        boxShadow: '0 6px 20px rgba(38, 38, 38, 0.2)',
      },
      arrow: {
        color: tx.tooltip,
      },
    },
  },
  MuiButtonBase: {
    defaultProps: {
      LinkComponent: LinkComponentMapper,
      disableRipple: true,
    },
  },
  // contained = the one lime action; outlined = tonal (the language has no outline buttons); text = ghost
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      size: 'medium',
      color: 'primary',
      loadingPosition: 'end',
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.buttonMedium,
        ...pressable,
        minWidth: 0,
        borderRadius: tx.rCtl,
        whiteSpace: 'nowrap',
        [`&.${buttonClasses.disabled}`]: {
          opacity: 0.4,
        },
      }),
      sizeMedium: {
        height: tx.h,
        padding: '0 20px',
        [`> .${buttonClasses.icon} > *:nth-of-type(1)`]: { fontSize: 18 },
      },
      sizeSmall: ({ theme }) => ({
        ...theme.typography.buttonSmall,
        height: 32,
        padding: '0 14px',
        [`> .${buttonClasses.icon} > *:nth-of-type(1)`]: { fontSize: 16 },
      }),
      // Flat lime: brighter on hover with no lift, the theme's deboss when pressed
      containedPrimary: {
        padding: '0 24px',
        backgroundColor: tx.lime,
        color: tx.onLime,
        boxShadow: 'none',
        '&:hover': { backgroundColor: tx.limeHover, boxShadow: 'none' },
        '&:active': { boxShadow: tx.deboss, transform: 'scale(0.985)' },
        [`&.${buttonClasses.disabled}`]: { backgroundColor: tx.lime, color: tx.onLime },
        [`> .${buttonClasses.loadingIndicator}`]: { color: tx.onLime },
        [`&.${buttonClasses.sizeSmall}`]: { padding: '0 16px' },
      },
      outlined: {
        border: 0,
        backgroundColor: tx.fill,
        color: tx.text,
        '&:hover': { border: 0, backgroundColor: tx.fill2 },
        [`&.${buttonClasses.disabled}`]: { border: 0, color: tx.text },
        [`> .${buttonClasses.loadingIndicator}`]: { color: tx.text },
      },
      text: {
        color: tx.text,
        '&:hover': { backgroundColor: tx.fill },
        [`&.${buttonClasses.disabled}`]: { color: tx.text },
      },
      ...STATUS_COLORS.reduce(
        (acc, color) => ({
          ...acc,
          [`contained${color[0].toUpperCase()}${color.slice(1)}`]: ({ theme }: { theme: Theme }) => {
            const paletteColor = theme.palette[color] as PaletteColor;
            return {
              backgroundColor: paletteColor.main,
              color: paletteColor.contrastText,
              boxShadow: 'none',
              '&:hover': { backgroundColor: paletteColor.light, boxShadow: 'none' },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: paletteColor.main,
                color: paletteColor.contrastText,
              },
            };
          },
          // a tonal or ghost button in a status colour keeps the tonal fill and takes the colour as its ink
          [`outlined${color[0].toUpperCase()}${color.slice(1)}`]: ({ theme }: { theme: Theme }) => ({
            color: color === 'error' ? tx.danger : (theme.palette[color] as PaletteColor).main,
          }),
          [`text${color[0].toUpperCase()}${color.slice(1)}`]: ({ theme }: { theme: Theme }) => ({
            color: color === 'error' ? tx.danger : (theme.palette[color] as PaletteColor).main,
          }),
        }),
        {},
      ),
      startIcon: {
        maxHeight: '1em',
        alignItems: 'center',
        marginLeft: -2,
      },
      endIcon: {
        maxHeight: '1em',
        alignItems: 'center',
        marginRight: -2,
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        ...pressable,
        borderRadius: 10,
        color: 'inherit',
        '&:hover': { backgroundColor: tx.fill },
      },
      sizeSmall: {
        borderRadius: 8,
      },
    },
  },
  // The segmented control: a tonal track with a pressed thumb
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: {
        backgroundColor: tx.fill,
        padding: 3,
        gap: 2,
        borderRadius: tx.rCtl,
        width: 'fit-content',
      },
      grouped: {
        border: 0,
        borderRadius: `${tx.rThumb}px !important`,
        margin: '0 !important',
      },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.buttonMedium,
        ...pressable,
        height: 34,
        padding: '0 14px',
        border: 0,
        color: tx.muted,
        '&:hover': { backgroundColor: tx.fill },
        '&.Mui-selected, &.Mui-selected:hover': selected,
      }),
      sizeSmall: {
        height: 26,
        padding: '0 10px',
        borderRadius: '7px !important',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: tx.text,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.buttonMedium,
        minHeight: tx.h,
        color: tx.muted,
        '&.Mui-selected': { color: tx.text },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.buttonSmall,
        height: 32,
        borderRadius: tx.rCtl,
        backgroundColor: tx.fill,
        color: tx.text,
        border: 0,
      }),
      sizeSmall: {
        height: 24,
        borderRadius: 7,
        fontSize: 12,
      },
      outlined: {
        border: 0,
        backgroundColor: tx.fill,
      },
      clickable: {
        ...pressable,
        '&:hover': { backgroundColor: tx.fill2 },
      },
      label: {
        paddingLeft: 12,
        paddingRight: 12,
      },
      labelSmall: {
        paddingLeft: 9,
        paddingRight: 9,
      },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: {
        borderRadius: 6,
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        fontSize: 10.5,
        fontWeight: 600,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'filled',
      size: 'medium',
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        ...theme.typography.inputMedium,
        color: tx.text,
        [`.${inputBaseClasses.sizeSmall} > &`]: {
          ...theme.typography.inputSmall,
        },
        '&::placeholder': {
          color: tx.faint,
          fontWeight: 500,
          opacity: 1,
        },
        // Covers both the settled post-fill state (:-webkit-autofill) and
        // Chrome's "actively previewing a suggestion" state
        // (:-internal-autofill-selected, a non-standard but widely-relied-on
        // Chromium pseudo-class) — without the latter, the solid blue
        // highlight still shows while a suggestion is highlighted/selected,
        // even though the settled state looks fine. The absurdly long
        // background-color transition is the standard trick for browsers
        // that ignore box-shadow/-webkit-text-fill-color on this pseudo-class.
        '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active, &:-internal-autofill-selected':
          {
            // box-shadow clips to the *input's own* border-radius, not its
            // wrapper's (.MuiFilledInput-root), so match the field's corners.
            borderRadius: 'inherit',
            // the inset shadow must be opaque to hide the autofill blue: the field's fill over the panel
            WebkitBoxShadow: `0 0 0 100px ${
              theme.palette.mode === 'dark' ? '#3A3A3A' : '#EEEEEE'
            } inset`,
            WebkitTextFillColor: theme.palette.text.primary,
            caretColor: theme.palette.text.primary,
            transition: 'background-color 600000s 0s, color 600000s 0s',
          },
      }),
    },
  },
  // Fields focus by the fill step alone: no ring, no underline
  MuiFilledInput: {
    defaultProps: {
      hiddenLabel: true,
      disableUnderline: true,
    },
    styleOverrides: {
      root: {
        backgroundColor: tx.fill,
        borderRadius: tx.rCtl,
        minHeight: tx.h,
        transition: `background-color ${tx.ease} .15s`,
        '&:hover': { backgroundColor: tx.fill2 },
        [`&.${inputBaseClasses.focused}`]: { backgroundColor: tx.fill2 },
        [`&.${inputBaseClasses.disabled}`]: { backgroundColor: tx.fill, opacity: 0.4 },
        [`&.${inputBaseClasses.sizeSmall}`]: { minHeight: 32 },
      },
      input: {
        height: 'auto',
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 12,
        paddingRight: 12,
        [`.${inputBaseClasses.sizeSmall} > &`]: {
          paddingTop: 7,
          paddingBottom: 7,
          paddingLeft: 10,
          paddingRight: 10,
        },
        [`.${inputBaseClasses.adornedStart} > &`]: {
          paddingLeft: 0,
        },
        [`.${inputBaseClasses.adornedEnd} > &`]: {
          paddingRight: 0,
        },
      },
      adornedStart: { paddingLeft: 12 },
      adornedEnd: { paddingRight: 8 },
      multiline: {
        padding: 0,
        alignItems: 'flex-start',
        [`&.${inputBaseClasses.adornedStart}, &.${inputBaseClasses.adornedEnd}`]: {
          [`> .${buttonBaseClasses.root}`]: {
            marginTop: 4,
          },
        },
      },
      error: ({ theme }) => ({
        boxShadow: `inset 0px 0px 0px 1px ${theme.palette.error.main}`,
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.subtitle2,
        color: tx.muted,
        '&.Mui-focused': { color: tx.muted },
      }),
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: {
        color: tx.faint,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.inputHelperSmall,
        color: tx.faint,
        marginTop: 6,
        marginLeft: 0,
        marginRight: 0,
      }),
      sizeSmall: ({ theme }) => ({
        ...theme.typography.inputHelperSmall,
        marginLeft: 0,
        marginRight: 0,
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      variant: 'filled',
      IconComponent: SelectIcon,
      MenuProps: {
        slotProps: {
          paper: {
            sx: {
              paddingLeft: 0,
              paddingRight: 0,
            },
          },
        },
      },
    },
    styleOverrides: {
      standard: {
        [`&.${inputBaseClasses.input}`]: {
          paddingRight: 28,
        },
      },
      icon: {
        color: tx.faint,
      },
      iconFilled: {
        right: 12,
      },
      iconStandard: {
        top: 'calc(50% - 8px)',
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: selectIcon,
    },
    styleOverrides: {
      inputRoot: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 12,
        [`&.${inputBaseClasses.sizeSmall}`]: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 10,
        },
      },
      paper: {
        ...floating,
        borderRadius: tx.rCtl,
        padding: 4,
        marginTop: 4,
        marginBottom: 4,
      },
      listbox: {
        padding: 0,
      },
      popupIndicator: {
        color: tx.faint,
      },
      option: ({ theme }) => ({
        ...theme.typography.inputMedium,
        minHeight: `${tx.h}px !important`,
        borderRadius: 8,
        '&.Mui-focused': { backgroundColor: `${tx.fill} !important` },
        '&[aria-selected="true"]': { ...selected },
      }),
      // Our shared Dialog component boosts its own z-index to
      // theme.zIndex.modal + 200 (see components/common/Dialog.tsx) so it can
      // stack above other modals — but Autocomplete's popper still portals at
      // MUI's default theme.zIndex.modal, so any address/search dropdown
      // opened inside one of our dialogs rendered behind the dialog itself.
      // Keep this comfortably above that boosted level.
      popper: ({ theme }) => ({
        zIndex: theme.zIndex.modal + 300,
      }),
    },
  },
  MuiCheckbox: {
    defaultProps: {
      icon: checkboxUncheckedIcon,
      checkedIcon: checkboxCheckedIcon,
      indeterminateIcon: checkboxIndeterminateIcon,
    },
    styleOverrides: {
      root: {
        '&:hover': { backgroundColor: 'transparent' },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: {
        color: tx.faint,
        '&.Mui-checked': { color: tx.text },
        '&:hover': { backgroundColor: 'transparent' },
      },
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        backgroundImage: 'none', // Disable background overlay
      },
    },
  },
  // In Hauser a Card is a top-level surface on the ground (it was the #262626 panel), so it takes the sheet recipe:
  // paper by day, slate at night. A group inside a sheet is the ds Card (a faint tile), not this.
  // The switch, Tradehaus's exactly (.tx-switch): 38 x 22 channel with 8px corners and a 16px square thumb (5px corners),
  // or 32 x 18 / 14px / 4px at size small for rows. Its materials come from the --tx-switch-* tokens per theme
  MuiSwitch: {
    defaultProps: { disableRipple: true },
    styleOverrides: {
      root: ({ ownerState }) => {
        const sm = ownerState.size === 'small';
        const w = sm ? 32 : 38;
        const h = sm ? 18 : 22;
        const t = sm ? 14 : 16;
        const inset = (h - t) / 2;
        return {
          width: w,
          height: h,
          padding: 0,
          overflow: 'visible',
          '& .MuiSwitch-switchBase': {
            padding: inset,
            '&:hover': { backgroundColor: 'transparent' },
            '&.Mui-checked': {
              transform: `translateX(${w - t - inset * 2}px)`,
              '& + .MuiSwitch-track': {
                background: 'var(--tx-switch-on)',
                boxShadow: 'var(--tx-switch-on-shadow)',
                opacity: 1,
              },
            },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
            '&.Mui-focusVisible + .MuiSwitch-track': { outline: '2px solid var(--tx-focus)', outlineOffset: 2 },
          },
          '& .MuiSwitch-thumb': {
            width: t,
            height: t,
            borderRadius: sm ? 4 : 5,
            background: 'var(--tx-switch-thumb)',
            boxShadow: 'var(--tx-switch-thumb-shadow)',
          },
          '& .MuiSwitch-track': {
            borderRadius: sm ? 7 : 8,
            background: 'var(--tx-switch-channel)',
            boxShadow: 'var(--tx-switch-channel-shadow)',
            opacity: 1,
            transition: 'background 0.18s ease',
          },
        };
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        maxWidth: '100%',
        borderRadius: tx.rSheet,
        backgroundColor: tx.panel,
        backgroundImage: tx.surfaceLight,
        backdropFilter: tx.blurPanel,
        WebkitBackdropFilter: tx.blurPanel,
        color: tx.text,
        boxShadow: tx.shadow,
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme, ownerState }) => {
        const { sx } = ownerState;
        return {
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(5),
          paddingTop: theme.spacing(5),
          paddingBottom: theme.spacing(5),
          paddingLeft: theme.spacing(5),
          paddingRight: theme.spacing(5),
          // MUI applies a different bottom padding value by using the `:last-child` pseudo-class
          // See https://github.com/mui/material-ui/blob/v6.4.4/packages/mui-material/src/CardContent/CardContent.js#L26
          // The only way to override it is to use `&:last-child` in the code too, which is cumbersome
          // This is a quick workaround that applies the same padding to the last child
          '&:last-child': {
            paddingBottom: theme.spacing(5),
            ...theme.unstable_sx({ ...sx }),
          },
        };
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: ({ theme }) => ({
        alignItems: 'center',
        padding: theme.spacing(5),
      }),
      title: ({ theme }) => ({
        ...theme.typography.cardTitle,
        color: tx.text,
      }),
      action: {
        alignSelf: 'center',
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(5),
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: tx.line,
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 50,
        paddingLeft: 0,
        paddingRight: 0,
        [theme.breakpoints.up('sm')]: {
          // Breakpoint override required so that the toolbar is consistent across all breakpoints
          minHeight: 50,
          paddingLeft: 0,
          paddingRight: 0,
        },
        alignItems: 'center',
      }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        ...floating,
        borderRadius: tx.rCtl,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        '&.MuiMenu-mobile': {
          backgroundColor: tx.backdrop,
        },
      },
      paper: ({ theme }) => ({
        ...floating,
        padding: 4,
        borderRadius: tx.rCtl,
        '.MuiMenu-mobile > &': {
          borderRadius: tx.rSheet,
          paddingLeft: theme.spacing(4),
          paddingRight: theme.spacing(4),
        },
      }),
      list: ({ theme }) => ({
        padding: 0,
        [`.MuiMenu-mobile & > .${menuItemClasses.root}`]: {
          '&.MuiMenu-mobileTitle': {
            ...theme.typography.overline,
            color: tx.muted,
            minHeight: 32,
            paddingTop: theme.spacing(3),
          },
        },
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.buttonMedium,
        lineHeight: 1.3,
        minHeight: tx.h,
        padding: '0 12px',
        borderRadius: 8,
        color: tx.text,
        '&:hover': { backgroundColor: tx.fill },
        [`&.${menuItemClasses.selected}, &.${menuItemClasses.selected}:hover`]: selected,
        [`&.${menuItemClasses.focusVisible}`]: { backgroundColor: tx.fill },
      }),
      dense: ({ theme }) => ({
        ...theme.typography.buttonSmall,
        minHeight: 34,
        padding: '0 10px',
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        width: 400,
        margin: theme.spacing(3),
        backgroundColor: tx.dialog,
        backgroundImage: tx.surfaceLight,
        backdropFilter: tx.blurPanel,
        WebkitBackdropFilter: tx.blurPanel,
        color: tx.text,
        borderRadius: tx.rSheet,
        boxShadow: '0 24px 64px rgba(0, 0, 0, 0.4)',
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        '&:not(.MuiBackdrop-invisible)': {
          backgroundColor: tx.backdrop,
        },
      },
    },
  },
  MuiSnackbar: {
    defaultProps: {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        bottom: theme.spacing(7.25),
        [theme.breakpoints.up('sm')]: {
          bottom: theme.spacing(7.25),
        },
      }),
    },
  },
  MuiAlert: {
    defaultProps: {
      variant: 'filled',
      iconMapping: {
        error: alertErrorIcon,
        success: alertSuccessIcon,
      },
    },
    styleOverrides: {
      root: ({ theme }) => ({
        width: 338,
        minHeight: 50,
        alignItems: 'center',
        paddingTop: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.5),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4.25),
        borderRadius: tx.rCtl,
        boxShadow: tx.shadowSm,
      }),
      filledError: ({ theme }) => ({
        color: theme.palette.error2.contrastText,
        backgroundColor: theme.palette.error2.main,
      }),
      filledSuccess: ({ theme }) => ({
        color: theme.palette.success2.contrastText,
        backgroundColor: theme.palette.success2.main,
      }),
      icon: ({ theme }) => ({
        alignSelf: 'center',
        padding: 0,
        marginRight: theme.spacing(5),
      }),
      message: ({ theme }) => ({
        ...theme.typography.body2,
        fontWeight: 600,
        flexGrow: 1,
        textAlign: 'center',
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: theme.spacing(4),
      }),
      action: {
        padding: 0,
        marginLeft: 0,
      },
    },
  },
  MuiAlertTitle: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: 'inherit',
        fontWeight: theme.typography.fontWeightBold,
        margin: 0,
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        backgroundColor: tx.fill2,
      },
      bar: {
        borderRadius: 999,
      },
    },
  },
  MuiPickersPopper: {
    styleOverrides: {
      paper: ({ theme }) => ({
        ...floating,
        padding: theme.spacing(4),
        borderRadius: tx.rTile,
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(2.5),
      }),
    },
  },
  MuiPickersLayout: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`.${dividerClasses.fullWidth}:not(.${dividerClasses.vertical})`]: {
          display: 'none',
        },
        [`.${dividerClasses.vertical}`]: {
          marginLeft: theme.spacing(4),
        },
      }),
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      root: {
        minHeight: 0,
        padding: 0,
        marginTop: 0,
      },
      labelContainer: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 600,
        color: tx.text,
      }),
      label: ({ theme }) => ({
        marginRight: theme.spacing(1),
      }),
      switchViewIcon: {
        opacity: 0.5,
      },
    },
  },
  MuiPickersArrowSwitcher: {
    styleOverrides: {
      spacer: {
        width: 16,
      },
    },
  },
  // Days are rounded squares: selected is the pressed well (the lifted tile on Dark), today is lime ink
  MuiPickersDay: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: CALENDAR_DAY_SIZE,
        height: CALENDAR_DAY_SIZE,
        fontSize: theme.typography.inputHelperSmall.fontSize,
        fontWeight: 500,
        color: tx.text,
        backgroundColor: 'transparent',
        borderRadius: tx.rCtl,
        margin: 0,
        '&:hover': { backgroundColor: tx.fill },
        [`&.${pickersDayClasses.today}:not(.${pickersDayClasses.selected})`]: {
          border: 0,
          color: tx.todayInk,
          fontWeight: 600,
        },
        [`&.${pickersDayClasses.selected}`]: {
          '&, &:hover, &:focus': {
            ...selected,
            fontWeight: 600,
          },
        },
      }),
    },
  },
  MuiDateCalendar: {
    defaultProps: {
      slotProps: {
        previousIconButton: {
          edge: false,
        },
        nextIconButton: {
          edge: false,
        },
      },
    },
    styleOverrides: {
      root: {
        width: '100%',
        height: '100%',
        maxHeight: '100%',
      },
      viewTransitionContainer: {
        flex: 1,
        '> div': {
          height: '100%',
        },
      },
    },
  },
  MuiDayCalendar: {
    styleOverrides: {
      weekDayLabel: ({ theme }) => ({
        width: CALENDAR_DAY_SIZE,
        height: CALENDAR_DAY_SIZE,
        fontSize: theme.typography.inputHelperSmall.fontSize,
        fontWeight: 500,
        color: tx.faint,
        margin: 0,
      }),
      slideTransition: {
        minHeight: CALENDAR_DAY_SIZE * 6, // 6 weeks
      },
      weekContainer: {
        margin: 0,
      },
    },
  },
  MuiDatePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: DatePickerOpenIcon,
      },
      slotProps: {
        openPickerButton: {
          edge: false,
        },
      },
    },
  },
  MuiDateTimePicker: {
    defaultProps: {
      slots: {
        openPickerIcon: DatePickerOpenIcon,
      },
      slotProps: {
        openPickerButton: {
          edge: false,
        },
      },
    },
  },
  MuiMultiSectionDigitalClockSection: {
    styleOverrides: {
      root: {
        width: 48,
      },
      item: ({ theme }) => ({
        width: 40,
        height: 40,
        fontSize: theme.typography.inputHelperSmall.fontSize,
        fontWeight: 500,
        borderRadius: 10,
        border: 0,
        marginTop: 0,
        marginBottom: 0,
        '&:first-of-type': {
          marginTop: 0,
        },
        [`&.${pickersDayClasses.selected}`]: {
          ...selected,
        },
      }),
    },
  },
};
